import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'

import { connect } from 'pwa-helpers/connect-mixin'

import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { notify } from '@operato/layout'
import { PageView, store } from '@operato/shell'
import { CommonGristStyles, CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { isMobileDevice } from '@operato/utils'
import { DataGrist, FetchOption } from '@operato/data-grist'
import { OxPrompt } from '@operato/popup/ox-prompt.js'

import { p13n } from '@operato/p13n'

@customElement('data-sensor-list-page')
export class DataSensorListPage extends connect(store)(p13n(localize(i18next)(PageView))) {
  static styles = [
    ScrollbarStyles,
    CommonGristStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        overflow: hidden;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() private gristConfig: any
  @state() private mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-sensor list'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-sensor',
      actions: [
        {
          icon: 'content_copy',
          title: i18next.t('button.copy'),
          action: this._copyDataSensor.bind(this)
        },
        {
          icon: 'save',
          title: i18next.t('button.save'),
          action: this._updateDataSensor.bind(this)
        },
        {
          icon: 'delete',
          title: i18next.t('button.delete'),
          action: this._deleteDataSensor.bind(this),
          emphasis: {
            danger: true
          }
        }
      ],
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'LIST' : 'GRID')

    return html`
      <ox-grist
        .mode=${mode}
        .config=${this.gristConfig}
        .fetchHandler=${this.fetchHandler.bind(this)}
        .personalConfigProvider=${this.getPagePreferenceProvider('ox-grist')!}
        ?url-params-sensitive=${this.active}
      >
        <div slot="headroom" class="header">
          <div class="filters">
            <ox-filters-form class="filter" autofocus without-search></ox-filters-form>
          </div>

          <ox-context-page-toolbar class="actions" .context=${this.context}></ox-context-page-toolbar>
        </div>

        <ox-grist-personalizer slot="setting"></ox-grist-personalizer>
      </ox-grist>
    `
  }

  async pageInitialized(lifecycle) {
    this.gristConfig = {
      list: { fields: ['name', 'description', 'active'] },
      columns: [
        { type: 'gutter', gutterName: 'sequence', fixed: true },
        { type: 'gutter', gutterName: 'row-selector', multiple: true, fixed: true },
        {
          type: 'string',
          name: 'name',
          label: true,
          header: i18next.t('field.name'),
          record: {
            editable: true
          },
          filter: 'search',
          sortable: true,
          width: 150,
          fixed: true
        },
        {
          type: 'string',
          name: 'description',
          label: true,
          header: i18next.t('field.description'),
          record: {
            editable: true
          },
          filter: 'search',
          width: 200
        },
        {
          type: 'checkbox',
          name: 'active',
          label: true,
          header: i18next.t('field.active'),
          record: {
            editable: true
          },
          sortable: true,
          filter: true,
          width: 60
        },
        {
          type: 'string',
          name: 'deviceId',
          label: true,
          header: i18next.t('field.device-id'),
          record: {
            editable: true
          },
          filter: 'search',
          sortable: true,
          width: 150
        },
        {
          type: 'varname',
          name: 'tag',
          label: true,
          header: i18next.t('field.tag'),
          record: {
            editable: true
          },
          sortable: true,
          filter: 'search',
          width: 150
        },
        {
          type: 'object',
          name: 'appliance',
          header: i18next.t('field.appliance'),
          record: {
            editable: true,
            options: {
              queryName: 'appliances'
            }
          },
          sortable: true,
          width: 120
        },
        {
          type: 'resource-object',
          name: 'decoder',
          header: {
            renderer: i18next.t('field.decoder-scenario')
          },
          record: {
            editable: true,
            options: {
              title: i18next.t('title.lookup scenario'),
              queryName: 'scenarios',
              columns: [
                { name: 'id', hidden: true },
                { name: 'name', header: i18next.t('field.name'), filter: 'search' },
                { name: 'description', header: i18next.t('field.description'), filter: 'search' }
              ],
              list: { fields: ['name', 'description'] }
            }
          },
          filter: false,
          width: 120
        },
        {
          type: 'object',
          name: 'dataSet',
          header: i18next.t('field.data-set'),
          record: {
            editable: true,
            options: {
              queryName: 'dataSets'
            }
          },
          sortable: true,
          width: 120
        },
        {
          type: 'string',
          name: 'refBy',
          label: true,
          header: i18next.t('field.ref-by'),
          record: {
            editable: true
          },
          sortable: true,
          width: 150
        },
        {
          type: 'string',
          name: 'model',
          label: true,
          header: i18next.t('field.model'),
          record: {
            editable: true
          },
          sortable: true,
          width: 150
        },
        {
          type: 'string',
          name: 'brand',
          label: true,
          header: i18next.t('field.brand'),
          record: {
            editable: true
          },
          sortable: true,
          width: 150
        },
        {
          type: 'string',
          name: 'serialNo',
          label: true,
          header: i18next.t('field.serial-no'),
          record: {
            editable: true
          },
          sortable: true,
          width: 150
        },
        {
          type: 'string',
          name: 'netmask',
          label: true,
          header: i18next.t('field.netmask'),
          record: {
            editable: true
          },
          sortable: true,
          width: 150
        },
        {
          type: 'number',
          name: 'capacity',
          label: true,
          header: i18next.t('field.capacity'),
          record: {
            editable: true
          },
          sortable: true,
          width: 100
        },
        {
          type: 'string',
          name: 'unit',
          label: true,
          header: i18next.t('field.unit'),
          record: {
            editable: true
          },
          sortable: true,
          width: 100
        },
        {
          type: 'object',
          name: 'updater',
          header: i18next.t('field.updater'),
          record: {
            editable: false
          },
          sortable: true,
          width: 120
        },
        {
          type: 'datetime',
          name: 'updatedAt',
          header: i18next.t('field.updated_at'),
          record: {
            editable: false
          },
          sortable: true,
          width: 180
        }
      ],
      rows: {
        selectable: {
          multiple: true
        }
      },
      sorters: [
        {
          name: 'name'
        }
      ]
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    const response = await client.query({
      query: gql`
        query ($filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!]) {
          responses: dataSensors(filters: $filters, pagination: $pagination, sortings: $sortings) {
            items {
              id
              name
              description
              active
              deviceId
              tag
              appliance {
                id
                name
                description
              }
              dataSet {
                id
                name
                description
              }
              decoder {
                id
                name
                description
              }
              refBy
              brand
              model
              serialNo
              netmask
              capacity
              unit
              updater {
                id
                name
              }
              updatedAt
            }
            total
          }
        }
      `,
      variables: {
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      total: response.data.responses.total || 0,
      records: response.data.responses.items || []
    }
  }

  async _deleteDataSensor() {
    if (
      await OxPrompt.open({
        title: i18next.t('text.are_you_sure'),
        text: i18next.t('text.sure_to_x', { x: i18next.t('text.delete') }),
        confirmButton: { text: i18next.t('button.confirm') },
        cancelButton: { text: i18next.t('button.cancel') }
      })
    ) {
      const ids = this.grist.selected.map(record => record.id)
      if (ids && ids.length > 0) {
        const response = await client.mutate({
          mutation: gql`
            mutation ($ids: [String!]!) {
              deleteDataSensors(ids: $ids)
            }
          `,
          variables: {
            ids
          }
        })

        if (!response.errors) {
          this.grist.fetch()
          notify({
            message: i18next.t('text.info_x_successfully', { x: i18next.t('text.delete') })
          })
        }
      }
    }
  }

  async _copyDataSensor() {
    var selected = this.grist.selected
    if (selected.length == 0) return

    if (!confirm(i18next.t('text.sure_to_x', { x: i18next.t('text.copy') }))) return
    var response = await client.mutate({
      mutation: gql`
        mutation ($ids: [String!]!) {
          copyDataSensors(ids: $ids) {
            id
          }
        }
      `,
      variables: {
        ids: selected.map(r => r.id)
      }
    })

    if (!response.errors) this.grist.fetch()
  }

  async _updateDataSensor() {
    let patches = this.grist.dirtyRecords
    if (patches && patches.length) {
      patches = patches.map(patch => {
        let patchField: any = patch.id ? { id: patch.id } : {}
        const dirtyFields = patch.__dirtyfields__
        for (let key in dirtyFields) {
          patchField[key] = dirtyFields[key].after
        }
        patchField.cuFlag = patch.__dirty__

        return patchField
      })

      const response = await client.mutate({
        mutation: gql`
          mutation ($patches: [DataSensorPatch!]!) {
            updateMultipleDataSensor(patches: $patches) {
              name
            }
          }
        `,
        variables: {
          patches
        }
      })

      if (!response.errors) this.grist.fetch()
    }
  }
}
